<script setup>
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { Button, Modal, Pagination, Spinner, Table } from '@drama/ui'
import { getDashboardRetentionUsers } from '../api/analytics.js'

const props = defineProps({ target: { type: Object, default: null }, kind: { type: String, required: true }, dates: { type: Array, required: true } })
const emit = defineEmits(['close'])
const { t, locale } = useI18n()
const date = ref('')
const page = ref(1)
const loading = ref(false)
const error = ref('')
const report = ref(null)
let request_id = 0
const text = (key, values) => t(`visualGame.dashboard.retention.drill.${key}`, values)
const columns = computed(() => ['user', 'type', 'first_at', 'retained', 'returned_at'].map((key, index) => ({ key, label: text(key), width: ['260px', '100px', '190px', '130px', '190px'][index] })))
function time(value) { return value ? new Date(value).toLocaleString(locale.value, { timeZone: 'UTC' }) : '—' }
async function load() {
  const id = ++request_id
  if (!props.target) { loading.value = false; return }
  loading.value = true
  error.value = ''
  report.value = null
  try {
    const result = await getDashboardRetentionUsers({ date: date.value, kind: props.kind, offset: props.target.offset, device_types: props.target.device_types, page: String(page.value) })
    if (id === request_id) report.value = result
  } catch (cause) { if (id === request_id) error.value = cause.message }
  finally { if (id === request_id) loading.value = false }
}
watch(() => props.target, target => {
  if (target) { date.value = target.date; page.value = 1 }
  void load()
})
function changeDate() { page.value = 1; void load() }
function changePage(value) { page.value = value; void load() }
</script>

<template>
  <Modal :open="Boolean(target)" :title="`${t(`visualGame.dashboard.retention.${kind}`)} · ${text('title')}`" width="1000px" @update:open="emit('close')">
    <template v-if="target">
      <label class="retention-users__date">{{ text('date') }}<select v-model="date" :disabled="loading" data-testid="retention-users-date" @change="changeDate"><option v-for="value in dates" :key="value" :value="value">{{ value }}</option></select></label>
      <p>{{ t(`visualGame.dashboard.retention.d${target.offset}`) }} · {{ text('note') }}</p>
      <div v-if="loading" role="status"><Spinner />{{ text('loading') }}</div>
      <div v-else-if="error" role="alert"><p>{{ error }}</p><Button @click="load">{{ text('retry') }}</Button></div>
      <template v-else-if="report">
        <p data-testid="retention-users-summary">{{ text('summary', { total: report.total, retained: report.retained_users === null ? t('visualGame.dashboard.retention.pending') : report.retained_users, date: report.return_date }) }}</p>
        <Table :columns="columns" :rows="report.items" row-key="user_id" data-testid="retention-users-table">
          <template #user="{ row }"><strong>{{ row.display_name || row.user_id }}</strong><small>{{ row.user_id }}</small><small v-if="row.email">{{ row.email }}</small></template>
          <template #type="{ row }">{{ text(row.account_type) }}</template>
          <template #first_at="{ row }">{{ time(row.first_at) }}</template>
          <template #retained="{ row }">{{ row.retained === null ? t('visualGame.dashboard.retention.pending') : text(row.retained ? 'yes' : 'no') }}</template>
          <template #returned_at="{ row }">{{ time(row.returned_at) }}</template>
        </Table>
        <p v-if="!report.items.length">{{ text('empty') }}</p>
        <Pagination :loading="loading" :has-prev="page > 1" :has-next="page * report.page_size < report.total" :summary="text('pager', { page, total: report.total })" :prev-label="text('prev')" :next-label="text('next')" @prev="changePage(page - 1)" @next="changePage(page + 1)" />
      </template>
    </template>
  </Modal>
</template>

<style scoped>
.retention-users__date { display: flex; align-items: center; gap: var(--drama-space-sm); }
select { padding: var(--drama-space-sm); background: var(--drama-color-surface); color: var(--drama-color-text); border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-sm); }
select:focus-visible { outline: 2px solid var(--drama-color-primary); }
small { display: block; color: var(--drama-color-text-muted); font-size: var(--drama-font-size-xs); }
</style>
